import type { CalibreCacheSnapshot } from '@watchkeeper/shared-types';
import { formatAccuracy } from './calibreTiers';
import {
  CACHE_DISPLAY_SECTIONS,
  type CacheDisplayField,
} from './refCalibreCache';
import { gateSnapshot, type SpecGates } from './effectiveSpecDisplay';

/** The Manufacturer row's label, when a brand-scoped tier replaces the base maker. */
export interface ManufacturerLabelOverride {
  label: string;
  /** The base calibre's manufacturer, kept for the row's tooltip. */
  baseName?: string;
}

/** Render one field's value as a short display string, resolving lookup ids and
 *  manufacturer ids to human labels (falling back to the raw value). */
function renderCacheValue(
  cache: CalibreCacheSnapshot,
  field: CacheDisplayField,
  resolveLookupLabel: (collection: string, raw: string) => string,
  manufacturerNameById: Record<string, string>,
  manufacturerOverride?: ManufacturerLabelOverride,
): string {
  if (field.manufacturer && manufacturerOverride) return manufacturerOverride.label;
  const v = cache[field.key];
  if (v === undefined || v === null || v === '') return '—';
  if (field.kind === 'boolean') return v ? 'Yes' : 'No';
  if (field.kind === 'array') return Array.isArray(v) ? v.join(', ') : String(v);
  if (field.manufacturer && typeof v === 'string') {
    return manufacturerNameById[v] ?? v;
  }
  if (field.lookup && typeof v === 'string') {
    return resolveLookupLabel(field.lookup, v);
  }
  return String(v);
}

/**
 * Primary "Effective Calibre Spec" panel — the source-of-truth view of a linked
 * calibre's EFFECTIVE spec (base + tier overrides). Reads the ref's populated
 * `calibreCache` when present, or a synthesized snapshot computed straight from
 * the linked calibre (see `calibreDataToCacheSnapshot`) so the spec shows the
 * instant a calibre is linked. Renders nothing when there's no snapshot at all.
 */
export function RefCalibreCachePanel({
  cache: rawCache,
  resolveLookupLabel,
  manufacturerNameById,
  onEditCalibre,
  gates,
  manufacturerOverride,
}: {
  cache: CalibreCacheSnapshot | undefined;
  resolveLookupLabel: (collection: string, raw: string) => string;
  manufacturerNameById: Record<string, string>;
  onEditCalibre?: () => void;
  /**
   * The tier's gates. Rows they make meaningless are hidden — the snapshot cannot
   * do this itself, since it carries no `hasDate` (see effectiveSpecDisplay).
   */
  gates?: SpecGates;
  /** Set under a brand-scoped tier: the brand is that tier's manufacturer. */
  manufacturerOverride?: ManufacturerLabelOverride;
}) {
  if (!rawCache) return null;
  const cache = gates ? gateSnapshot(rawCache, gates) : rawCache;

  const accuracy = formatAccuracy(
    cache.accuracySymmetric,
    cache.accuracyLowerSecPerDay,
    cache.accuracyUpperSecPerDay,
  );
  const hasAccuracy = accuracy !== '—';

  return (
    <div className='mt-2 rounded-lg border-2 border-brand-200 bg-brand-50/50 p-4 space-y-3 shadow-sm'>
      <div className='flex items-center gap-2'>
        <div className='text-sm font-semibold text-gray-800'>
          Effective Calibre Spec
        </div>
        {cache.tierName && (
          <span className='rounded bg-brand-100 text-brand-700 px-1.5 py-0.5 text-[9px] font-semibold uppercase'>
            tier · {cache.tierName}
          </span>
        )}
        {onEditCalibre && (
          <button
            type='button'
            onClick={onEditCalibre}
            className='ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors'
            title='Edit the linked calibre (opens in a modal)'
          >
            <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
            </svg>
            Edit calibre
          </button>
        )}
      </div>
      {CACHE_DISPLAY_SECTIONS.map((section) => {
        const present = section.fields.filter((f) => {
          if (f.manufacturer && manufacturerOverride) return true;
          const v = cache[f.key];
          if (v === undefined || v === null || v === '') return false;
          if (Array.isArray(v) && v.length === 0) return false;
          return true;
        });
        // Append the grouped accuracy row to the Specs section when present.
        const showAccuracyHere = section.title === 'Specs' && hasAccuracy;
        if (present.length === 0 && !showAccuracyHere) return null;
        return (
          <div key={section.title}>
            <div className='text-[10px] font-semibold text-gray-400 uppercase mb-1'>
              {section.title}
            </div>
            <div className='grid grid-cols-2 gap-x-3 gap-y-1'>
              {present.map((f) => {
                const brandTierMaker = f.manufacturer && manufacturerOverride;
                return (
                  <div key={f.key} className='flex items-center gap-1.5 text-xs'>
                    <span className='text-gray-500 truncate'>{f.label}</span>
                    <span
                      className={`ml-auto font-medium text-right ${
                        brandTierMaker ? 'text-brand-700' : 'text-gray-700'
                      }`}
                      title={
                        brandTierMaker && manufacturerOverride?.baseName
                          ? `Brand-scoped tier — base movement by ${manufacturerOverride.baseName}`
                          : undefined
                      }
                    >
                      {renderCacheValue(
                        cache,
                        f,
                        resolveLookupLabel,
                        manufacturerNameById,
                        manufacturerOverride,
                      )}
                    </span>
                    {brandTierMaker && (
                      <span className='shrink-0 rounded bg-brand-100 text-brand-700 px-1 py-0.5 text-[9px] font-semibold uppercase'>
                        tier
                      </span>
                    )}
                  </div>
                );
              })}
              {showAccuracyHere && (
                <div className='flex items-center gap-1.5 text-xs'>
                  <span className='text-gray-500 truncate'>Accuracy</span>
                  <span className='ml-auto font-medium text-gray-700 text-right'>
                    {accuracy}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
